import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function toISO(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function todayISO(): string {
  const d = new Date();
  return toISO(d.getFullYear(), d.getMonth(), d.getDate());
}

interface CalendarPickerProps {
  value: string;
  onChange: (date: string) => void;
  min?: string;
  label?: string;
}

export function CalendarPicker({ value, onChange, min, label }: CalendarPickerProps) {
  const today = todayISO();
  const initial = value || today;
  const [year, setYear]   = useState(() => parseInt(initial.slice(0, 4)));
  const [month, setMonth] = useState(() => parseInt(initial.slice(5, 7)) - 1);

  const daysInMonth   = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  const cells: (number | null)[] = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div>
      {label && (
        <p className="block text-[11px] font-bold text-muted uppercase tracking-widest mb-2">
          {label}
        </p>
      )}
      <div className="rounded-xl bg-bg p-4">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-3">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={prevMonth}
            className="rounded-full !text-muted hover:!text-text"
            aria-label="Previous month"
          >
            <ChevronLeft size={15} />
          </Button>
          <p className="text-sm font-bold text-text">
            {MONTHS[month]} {year}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={nextMonth}
            className="rounded-full !text-muted hover:!text-text"
            aria-label="Next month"
          >
            <ChevronRight size={15} />
          </Button>
        </div>

        {/* Day-of-week header */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map((d) => (
            <div key={d} className="flex items-center justify-center h-8">
              <span className="text-[10px] font-bold text-muted">{d}</span>
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (!day) return <div key={`blank-${i}`} />;
            const iso      = toISO(year, month, day);
            const isMin    = min ? iso < min : false;
            const isToday  = iso === today;
            const selected = iso === value;

            let cls = 'flex items-center justify-center h-8 w-8 mx-auto text-sm transition-colors rounded-full ';
            if (isMin) {
              cls += 'opacity-30 cursor-not-allowed pointer-events-none text-muted';
            } else if (selected) {
              cls += 'bg-accent text-black font-bold cursor-pointer';
            } else if (isToday) {
              cls += 'border border-accent text-accent font-bold cursor-pointer hover:bg-accent hover:text-black';
            } else {
              cls += 'text-text cursor-pointer hover:bg-surface-raised';
            }

            return (
              <div key={iso} className="flex items-center justify-center py-0.5">
                <button
                  type="button"
                  className={cls}
                  onClick={() => !isMin && onChange(iso)}
                  disabled={isMin}
                  aria-label={`${MONTHS[month]} ${day}, ${year}`}
                  aria-selected={selected}
                  aria-current={isToday ? 'date' : undefined}
                >
                  {day}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
